/**
 * Life4Earth V2 — User Theme Preference API
 *
 * Endpoints for reading and persisting the user's theme + texture choices.
 * Uses the standard apiFetch client with fake auth.
 */
import { useCallback } from 'react';
import { apiFetch, getCurrentUserGuid } from './client';

interface ThemeResponse {
  themePreference: string;
  backgroundTexture: string;
}

/**
 * Hook returning helpers to GET / PUT the user's theme and texture preferences.
 *
 * Usage:
 *   const { fetchTheme, updateTheme, updateTexture } = useUserThemeApi();
 *   const pref = await fetchTheme();          // { theme: 'dark', texture: 'svgHexGrid' }
 *   await updateTheme('light');
 *   await updateTexture('svgCircuit');
 */
export function useUserThemeApi() {
  const fetchTheme = useCallback(async (): Promise<{ theme: string; texture: string }> => {
    try {
      const guid = getCurrentUserGuid();
      const data = await apiFetch<ThemeResponse>(`/api/security/users/${guid}/theme`);
      return {
        theme: data.themePreference || 'dark',
        texture: data.backgroundTexture || 'solid',
      };
    } catch {
      return { theme: 'dark', texture: 'solid' }; // fallback
    }
  }, []);

  const updateTheme = useCallback(async (theme: string): Promise<void> => {
    const guid = getCurrentUserGuid();
    await apiFetch(`/api/security/users/${guid}/theme`, {
      method: 'PUT',
      body: JSON.stringify({ themePreference: theme }),
    });
  }, []);

  /** Persist background texture preference to the server. */
  const updateTexture = useCallback(async (texture: string): Promise<void> => {
    const guid = getCurrentUserGuid();
    // Fetch current theme so we don't clobber it (themePreference is required)
    const current = await apiFetch<ThemeResponse>(`/api/security/users/${guid}/theme`);
    await apiFetch(`/api/security/users/${guid}/theme`, {
      method: 'PUT',
      body: JSON.stringify({
        themePreference: current.themePreference || 'dark',
        backgroundTexture: texture,
      }),
    });
  }, []);

  return { fetchTheme, updateTheme, updateTexture };
}
